/**
 * FlowEditor.tsx
 * フローエディタのメインコンポーネント
 * ノードの配置、接続、実行を行うビジュアルエディタを提供する
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type Connection,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './reactflow-overrides.css';

import { useAuth } from '@/auth/AuthProvider';
import { useFiloRemote } from '@/connection/FiloRemoteContext';
import {
  FLOW_EFFECT_RPC_TIMEOUT_MS,
  type BatchRobotService,
} from '@/connection/BatchRobotService';
import type { FlowRunTargets } from '@/connection/MultiRobotConnectionManager';
import { Bot, ChevronDown, ChevronRight, ChevronUp, Home, Minus } from 'lucide-react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { type AppNode, type AppNodeOf, type AppEdge, type NodeKind } from './types';
import {
  isNodeKind,
  getNodeTypes,
  getDefaultData,
  getHelp,
  getSettings,
} from './nodeRegistry';
import { NodePalette } from './NodePalette';
import { FlowCanvas } from './components/FlowCanvas';
import { FlowToolbar, type FlowRunMode } from './components/FlowToolbar';
import { FlowInspectorPanel } from './components/FlowInspectorPanel';
import { ContextMenu } from './components/ContextMenu';
import { QrScannerDialog } from './components/QrScannerDialog';
import { ListenDialog } from './components/ListenDialog';
import { findOfflineFlowIssue, getOfflineQrSpeechMessage, isOfflineUnsupportedConnection, runFlow } from './run';
import { OFFLINE_MODE } from '@/config/offline';
import { validateFlowConnectivity } from './validateFlowConnectivity';
import { getInitialFunctionNodes, getInitialNodes, isFixedNodeId } from './config/fixedNodes';
import { createFlowHistorySnapshot, useFlowHistory, useFlowLogger, useContextMenu } from './hooks';
import type { FlowHistorySnapshot } from './hooks';
import { getCurrentFlowLayoutPreset } from './config/layoutPreset';
import { getNodeDisplayLabel, FlowDisplayLanguageProvider, type FlowDisplayLanguage } from './displayLanguage';
import type { ListenResult, QrScanResult, QrScanner, SpeechListener } from './types';
import { setFlowMotionAtom } from './atoms/flowMotionAtom';
import { setFlowSayTextAtom } from './atoms/flowSayTextAtom';
import { setFlowTofAtom } from './atoms/flowTofAtom';
import { introductionPresentationAtom } from './atoms/introductionPresentationAtom';
import { nodeSettingsNodeIdAtom } from './atoms/nodeSettingsAtom';
import { functionWorkspaceRequestAtom } from './atoms/functionWorkspaceAtom';
import { clearSavedFlow, loadFlow, loadFunction, saveFlow, saveFunction } from './persistence';
import { acquireQrCameraLease, QrScanCoordinator } from './qrScanCoordinator';
import {
  acquireListenCameraLease,
  enableListenMicrophone,
  ListenCoordinator,
  waitForLiveAudioStream,
} from './listenCoordinator';
import { stopFlowExecution } from './flowStop';
import { FlowSourceViewer } from './components/FlowSourceViewer';
import { NodeSettingsDialog } from './components/NodeSettingsDialog';
import { closeGestureCreator, useGestureCreatorRequest } from './config/gestureCreator';
import { GestureCreatorOverlay } from '@/apps/gesture/GestureCreatorOverlay';
import { generateFlowSource } from './source/generateFlowSource';
import { createTranscribeCredentialsProvider } from '@/speech/transcribeCredentials';
import { unlockSpeechAudioContext } from '@/speech/speechAudioContext';
import { ProductTour, type ProductTourStep } from '@/components/ProductTour';
import { playSimulationSoundEffect } from './simulationSoundPlayer';
import { createSharedFlowSnapshot, type SharedFlowSnapshot } from './realtimeFlow';
import { duplicateSharedFlow, replaceWithSharedFlow, type SharedFlowCopyMode } from './duplicateSharedFlow';

const initialEdges: AppEdge[] = [];
const nodeTypes = getNodeTypes();
const LISTENING_LED = { red: 138, green: 0, blue: 255, mode: 1 } as const;
const LED_OFF = { red: 0, green: 0, blue: 0, mode: 0 } as const;
const SIMULATION_LED_OFF_COLOR = '#000000';
const LISTEN_RPC_TIMEOUT_MS = 3000;
const VirtualRobot = lazy(() => import('./components/VirtualRobot').then((module) => ({ default: module.VirtualRobot })));
const createSimulationRunTargets = (): FlowRunTargets => ({
  runId: `simulation-${crypto.randomUUID()}`,
  robotIds: [],
  generations: {},
});
const TAP_ADD_OFFSETS: Array<{ x: number; y: number }> = [
  { x: 0, y: 0 },
  { x: 26, y: 0 },
  { x: -26, y: 0 },
  { x: 0, y: 26 },
  { x: 0, y: -26 },
  { x: 20, y: 20 },
  { x: -20, y: -20 },
  { x: 20, y: -20 },
  { x: -20, y: 20 },
];
const FLOW_TOUR_STEPS: ProductTourStep[] = [
  {
    target: '[data-product-tour="flow-canvas"]',
    title: 'フローチャートをつくる',
    description: 'カードを配置して線でつなぐと、ロボットが動く順番を組み立てられます。',
  },
  {
    target: '[data-product-tour="flow-cards"]',
    title: 'カードを選ぶ',
    description: '画面下部のカード一覧から、話す、QR読み取り、条件分岐、Counterなどをキャンバスへ追加します。',
  },
  {
    target: '[data-product-tour="flow-edit"]',
    title: 'カードをつなぐ・消す',
    description: '「つなぐ」で処理順を結び、選択したカードや線は「ごみばこ」で削除できます。',
  },
  {
    target: '[data-product-tour="flow-run"]',
    title: 'フローを動かす',
    description: 'テストで確認するか、接続した実機でフローを実行します。',
  },
];

export type DuplicateFlowRequest = {
  id: string;
  mode: SharedFlowCopyMode;
  snapshot: SharedFlowSnapshot;
};

type FlowEditorProps = {
  duplicateFlowRequest?: DuplicateFlowRequest | null;
  onWorkspaceContextChange?: (context: 'edit' | 'simulation' | 'robots') => void;
  onFlowSnapshotChange?: (snapshot: SharedFlowSnapshot) => void;
  onDisplayLanguageChange?: (language: FlowDisplayLanguage) => void;
  toolbarSlots?: {
    brand?: ReactNode;
    trailing?: ReactNode;
  };
  robotCameraView?: {
    active: boolean;
    docked: boolean;
    // hidden はレイヤーが視覚的に隠れている間 true（最小化・3Dシミュレーション実行中）。
    // マウントは維持したまま音声などを抑制するために render 側へ伝える
    render: (view: { hidden: boolean }) => ReactNode;
  };
};

type WorkspaceFrame = {
  functionNodeId: string;
  functionName: string;
  nodes: AppNode[];
  edges: AppEdge[];
};

const materializeWorkspaceTree = (
  userSub: string,
  frames: WorkspaceFrame[],
  activeNodes: AppNode[],
  activeEdges: AppEdge[],
) => {
  let childNodes = activeNodes;
  let childEdges = activeEdges;

  for (let index = frames.length - 1; index >= 0; index -= 1) {
    const frame = frames[index];
    saveFunction(userSub, frame.functionName, childNodes, childEdges);
    childNodes = frame.nodes.map((node) => node.id === frame.functionNodeId && node.type === 'function'
      ? {
          ...node,
          data: { ...node.data, internalNodes: childNodes, internalEdges: childEdges },
        }
      : node) as AppNode[];
    childEdges = frame.edges;
  }

  return { nodes: childNodes, edges: childEdges };
};

/**
 * フローエディタの内部コンポーネント
 * ReactFlowProvider内で動作する必要がある
 */
function FlowEditorInner({
  duplicateFlowRequest,
  onWorkspaceContextChange,
  onFlowSnapshotChange,
  onDisplayLanguageChange,
  toolbarSlots,
  robotCameraView,
}: FlowEditorProps) {
  const { user } = useAuth();
  const userSub = user!.sub;
  const initialPresetRef = useRef(getCurrentFlowLayoutPreset());
  const initialTouchModeRef = useRef(
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  );
  const tapAddCountRef = useRef(0);
  const lastDuplicateRequestIdRef = useRef<string | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const sourceButtonRef = useRef<HTMLButtonElement>(null);
  const [currentPreset, setCurrentPreset] = useState(initialPresetRef.current);
  const [isTouchMode, setTouchMode] = useState(initialTouchModeRef.current);
  const isMobile = currentPreset === 'mobile';
  const enableTapPaletteAdd = isTouchMode || currentPreset !== 'desktop';
  const [restoredFlow] = useState(() => loadFlow(userSub));

  // ノードとエッジの状態管理（React Flow提供のフック）
  const [nodes, setNodes, onNodesChange] = useNodesState<AppNode>(
    restoredFlow?.nodes ?? getInitialNodes(initialPresetRef.current)
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState<AppEdge>(restoredFlow?.edges ?? initialEdges);
  const isNodeDragInProgressRef = useRef(false);
  const latestFlowSnapshotRef = useRef(createFlowHistorySnapshot(nodes, edges));
  latestFlowSnapshotRef.current = createFlowHistorySnapshot(nodes, edges);
  const {
    canUndo,
    canRedo,
    commit: commitHistory,
    undo: undoHistory,
    redo: redoHistory,
  } = useFlowHistory(createFlowHistorySnapshot(
    restoredFlow?.nodes ?? getInitialNodes(initialPresetRef.current),
    restoredFlow?.edges ?? initialEdges,
  ));
  const [workspaceStack, setWorkspaceStack] = useState<WorkspaceFrame[]>([]);

  // 選択中のノードID（説明パネル表示用）
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [settingsNodeId, setSettingsNodeId] = useAtom(nodeSettingsNodeIdAtom);
  const [functionWorkspaceRequest, setFunctionWorkspaceRequest] = useAtom(functionWorkspaceRequestAtom);
  const [isPaletteOpen, setPaletteOpen] = useState(false);
  const [isInspectorOpen, setInspectorOpen] = useState(false);
  const [isBottomInspectorOpen, setBottomInspectorOpen] = useState(false);
  const [showCardNames, setShowCardNames] = useState(true);
  const [isRobotPreviewMinimized, setRobotPreviewMinimized] = useState(false);
  const [displayLanguageState, setDisplayLanguage] = useState<FlowDisplayLanguage>('en');
  const displayLanguage: FlowDisplayLanguage = displayLanguageState === 'en' ? 'en' : 'ja';
  const [isQrScannerOpen, setQrScannerOpen] = useState(false);
  const [isListenOpen, setListenOpen] = useState(false);
  const [listenStream, setListenStream] = useState<MediaStream | null>(null);
  const [isConnectMode, setConnectMode] = useState(false);
  const [isSourceViewOpen, setSourceViewOpen] = useState(false);
  const [isGuideOpen, setGuideOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | 'error'>('saved');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const [runWarning, setRunWarning] = useState<string | null>(null);
  const [activeNodeIds, setActiveNodeIds] = useState<Set<string>>(() => new Set());
  const [errorNodeIds, setErrorNodeIds] = useState<Set<string>>(() => new Set());
  const [activeRunMode, setActiveRunMode] = useState<FlowRunMode | null>(null);
  const [runTargetCount, setRunTargetCount] = useState<number | null>(null);
  const [simulationEyeColor, setSimulationEyeColor] = useState(SIMULATION_LED_OFF_COLOR);

  const setSimulationLightColor = useCallback((displayColor: string) => {
    setSimulationEyeColor(displayColor);
  }, []);
  const isSimulationRunning = activeRunMode === 'simulation' && isRunning;
  const hasDockedCamera = Boolean(robotCameraView?.active && robotCameraView.docked);
  const showDockedCamera = hasDockedCamera && !isRobotPreviewMinimized && !isSimulationRunning;
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeRunModeRef = useRef<FlowRunMode | null>(null);
  const runTargetUnsubscribeRef = useRef<(() => void) | null>(null);
  const activeBatchServiceRef = useRef<BatchRobotService | null>(null);
  const stopSpeakPromiseRef = useRef<Promise<void> | null>(null);
  const ledCleanupPromiseRef = useRef<Promise<void> | null>(null);
  const cleanupRemoteLedsRef = useRef<() => void>(() => undefined);
  const isMountedRef = useRef(true);
  const qrScanCoordinatorRef = useRef(new QrScanCoordinator());
  const qrScanCoordinator = qrScanCoordinatorRef.current;
  const listenCoordinatorRef = useRef(new ListenCoordinator());
  const listenCoordinator = listenCoordinatorRef.current;

  // React Flowのユーティリティ関数
  const { screenToFlowPosition, getNode, fitView } = useReactFlow<AppNode, AppEdge>();

  const {
    snapshot: remoteSnapshot,
    connectedCount,
    representativeStream,
    acquireRepresentativeCamera,
    migrateRepresentativeCamera,
    releaseCamera,
    callRobot,
    createTranscribeCredentials,
    playSimulationSpeech,
    synthesizeSpeech,
    createRunSnapshot,
    createBatchService,
    setRunInProgress,
    subscribeRunTargets,
    publishFlow,
  } = useFiloRemote();
  const releaseCameraRef = useRef(releaseCamera);
  const callRobotRef = useRef(callRobot);
  const createTranscribeCredentialsRef = useRef(createTranscribeCredentials);
  const representativeStreamRef = useRef(representativeStream);
  const measureTofRef = useRef<(() => number) | null>(null);
  createTranscribeCredentialsRef.current = createTranscribeCredentials;
  representativeStreamRef.current = representativeStream;

  const getTranscribeCredentials = useMemo(
    () => createTranscribeCredentialsProvider({
      createTranscribeCredentials: () => createTranscribeCredentialsRef.current(),
    }),
    [],
  );

  // Jotai: VirtualRobot でモーション再生するための atom setter
  const setFlowMotion = useSetAtom(setFlowMotionAtom);
  // Jotai: VirtualRobot でふきだし表示するための atom setter
  const setSayText = useSetAtom(setFlowSayTextAtom);
  const isIntroductionPresentation = useAtomValue(introductionPresentationAtom);
  const setTofScan = useSetAtom(setFlowTofAtom);

  const onTofMeasureReady = useCallback((measure: (() => number) | null) => {
    measureTofRef.current = measure;
  }, []);

  // カスタムフック：ログ管理とコンテキストメニュー
  const { logs, log, clearLogs, flushLogs } = useFlowLogger();
  const { contextMenu, showContextMenu, hideContextMenu } = useContextMenu();

  useEffect(() => {
    onWorkspaceContextChange?.(
      isRunning && activeRunMode === 'simulation'
        ? 'simulation'
        : isRunning && activeRunMode === 'robots'
          ? 'robots'
          : 'edit',
    );
  }, [activeRunMode, isRunning, onWorkspaceContextChange]);

  // 選択中のノードオブジェクトを取得
  const selectedNode = useMemo(
    () => (selectedNodeId ? nodes.find((node) => node.id === selectedNodeId) : undefined),
    [nodes, selectedNodeId],
  );

  // ダイアログは値の変更を即座に映す必要があるため、getNode ではなく nodes から引く
  const settingsNode = useMemo(
    () => (settingsNodeId ? nodes.find((node) => node.id === settingsNodeId) : undefined),
    [nodes, settingsNodeId],
  );
  const settingsKind = settingsNode && isNodeKind(settingsNode.type) ? settingsNode.type : undefined;
  const settingsContent = settingsKind ? getSettings(settingsKind) : undefined;

  // 選択中ノードのヘルプ情報を取得
  const selectedKind = selectedNode && isNodeKind(selectedNode.type) ? selectedNode.type : undefined;
  const help = selectedKind ? getHelp(selectedKind) : undefined;
  const generatedSource = useMemo(
    () => generateFlowSource(nodes, edges),
    [edges, nodes],
  );

  useEffect(() => {
    const onResize = () => {
      const next = getCurrentFlowLayoutPreset();
      setCurrentPreset((prev) => (prev === next ? prev : next));
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(pointer: coarse)');
    const onChange = (event: MediaQueryListEvent) => {
      setTouchMode(event.matches);
    };
    media.addEventListener('change', onChange);
    setTouchMode(media.matches);

    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setPaletteOpen(false);
      setInspectorOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isNodeDragInProgressRef.current) return;
    commitHistory(createFlowHistorySnapshot(nodes, edges));
  }, [commitHistory, edges, nodes]);

  useEffect(() => {
    onFlowSnapshotChange?.(createSharedFlowSnapshot(0, nodes, edges));
  }, [edges, nodes, onFlowSnapshotChange]);

  useEffect(() => {
    onDisplayLanguageChange?.(displayLanguage);
  }, [displayLanguage, onDisplayLanguageChange]);

  const onNodeDragStart = useCallback(() => {
    isNodeDragInProgressRef.current = true;
  }, []);

  const onNodeDragStop = useCallback((
    _event: MouseEvent | TouchEvent,
    draggedNode: AppNode,
    draggedNodes: AppNode[],
  ) => {
    isNodeDragInProgressRef.current = false;
    const finalDraggedNodes = new Map(
      [...draggedNodes, draggedNode].map((node) => [node.id, node]),
    );
    const latestSnapshot = latestFlowSnapshotRef.current;
    commitHistory(createFlowHistorySnapshot(
      latestSnapshot.nodes.map((node) => finalDraggedNodes.get(node.id) ?? node),
      latestSnapshot.edges,
    ));
  }, [commitHistory]);

  const saveCurrentFlow = useCallback(() => {
    const rootFlow = materializeWorkspaceTree(userSub, workspaceStack, nodes, edges);
    const result = saveFlow(userSub, rootFlow.nodes, rootFlow.edges);
    publishFlow(rootFlow.nodes, rootFlow.edges);
    if (result.success) {
      setSaveStatus('saved');
      setSavedAt(new Date(result.savedAt).toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
      }));
      setSaveError(null);
    } else {
      setSaveStatus('error');
      setSaveError(result.error);
    }
  }, [edges, nodes, publishFlow, userSub, workspaceStack]);

  useEffect(() => {
    if (isRunning) {
      return;
    }

    setSaveStatus('saving');
    const timeoutId = window.setTimeout(() => {
      saveCurrentFlow();
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isRunning, saveCurrentFlow]);

  useEffect(() => {
    if (
      !duplicateFlowRequest
      || duplicateFlowRequest.id === lastDuplicateRequestIdRef.current
      || isRunning
    ) return;

    const rootFlow = materializeWorkspaceTree(userSub, workspaceStack, nodes, edges);
    const duplicated = duplicateFlowRequest.mode === 'replace'
      ? replaceWithSharedFlow(duplicateFlowRequest.snapshot)
      : duplicateSharedFlow(rootFlow.nodes, rootFlow.edges, duplicateFlowRequest.snapshot);
    lastDuplicateRequestIdRef.current = duplicateFlowRequest.id;
    if (duplicated.addedNodeCount === 0) return;

    setWorkspaceStack([]);
    setNodes(duplicated.nodes);
    setEdges(duplicated.edges);
    setSelectedNodeId(null);
    setSettingsNodeId(null);
    setConnectMode(false);
    clearLogs();
    setActionFeedback(duplicateFlowRequest.mode === 'replace'
      ? '閲覧フローで置き換えました'
      : `${duplicated.addedNodeCount}枚のカードを追加しました`);
    hideContextMenu();
    window.requestAnimationFrame(() => {
      void fitView({ padding: 0.08, duration: 400 });
    });
  }, [
    clearLogs,
    duplicateFlowRequest,
    edges,
    fitView,
    hideContextMenu,
    isRunning,
    nodes,
    setEdges,
    setNodes,
    setSettingsNodeId,
    userSub,
    workspaceStack,
  ]);

  useEffect(() => {
    if (!functionWorkspaceRequest || isRunning) return;
    const functionNode = nodes.find((node): node is AppNodeOf<'function'> =>
      node.id === functionWorkspaceRequest && node.type === 'function'
    );
    setFunctionWorkspaceRequest(null);
    if (!functionNode) return;

    const functionName = functionNode.data.functionName.trim();
    if (!functionName) return;
    const savedFunction = loadFunction(userSub, functionName);
    const hasCardDefinition = functionNode.data.internalNodes.length > 0;
    const nextFlow = hasCardDefinition
      ? { nodes: functionNode.data.internalNodes, edges: functionNode.data.internalEdges }
      : savedFunction ?? { nodes: getInitialFunctionNodes(currentPreset), edges: [] };

    setWorkspaceStack((current) => current.concat({
      functionNodeId: functionNode.id,
      functionName,
      nodes,
      edges,
    }));
    setNodes(nextFlow.nodes);
    setEdges(nextFlow.edges);
    setSelectedNodeId(null);
    setSettingsNodeId(null);
    setConnectMode(false);
    hideContextMenu();
  }, [
    currentPreset,
    edges,
    functionWorkspaceRequest,
    hideContextMenu,
    isRunning,
    nodes,
    setEdges,
    setFunctionWorkspaceRequest,
    setNodes,
    setSettingsNodeId,
    userSub,
  ]);

  const returnToRootWorkspace = useCallback(() => {
    if (workspaceStack.length === 0 || isRunning) return;
    const rootFlow = materializeWorkspaceTree(userSub, workspaceStack, nodes, edges);
    saveFlow(userSub, rootFlow.nodes, rootFlow.edges);
    setWorkspaceStack([]);
    setNodes(rootFlow.nodes);
    setEdges(rootFlow.edges);
    setSelectedNodeId(null);
    setSettingsNodeId(null);
    setConnectMode(false);
    hideContextMenu();
  }, [edges, hideContextMenu, isRunning, nodes, setEdges, setNodes, setSettingsNodeId, userSub, workspaceStack]);

  const createNode = useCallback((
    kind: NodeKind,
    position: { x: number; y: number }
  ): AppNode => {
    const id = `node-${crypto.randomUUID()}`;
    return {
      id,
      type: kind,
      position,
      data: getDefaultData(kind),
      dragHandle: '.node-drag-handle',
    } as AppNode;
  }, []);

  const appendNode = useCallback((kind: NodeKind, position: { x: number; y: number }) => {
    if (kind === 'function' && workspaceStack.length > 0) {
      return;
    }
    const newNode = createNode(kind, position);
    setNodes((nds) => nds.concat(newNode));
    if (kind === 'function') {
      setSettingsNodeId(newNode.id);
    }
  }, [createNode, setNodes, setSettingsNodeId, workspaceStack.length]);

  /** ノード間の接続時に呼ばれるハンドラ */
  const onConnect = useCallback(
    (connection: Connection) => {
      if (OFFLINE_MODE && isOfflineUnsupportedConnection(getNode(connection.source)?.type, getNode(connection.target)?.type)) {
        setRunWarning(getOfflineQrSpeechMessage(displayLanguage));
        return;
      }
      setEdges((eds) => addEdge({ ...connection, interactionWidth: 20, deletable: true }, eds));
    },
    [displayLanguage, getNode, setEdges]
  );

  /** ドラッグオーバー時のハンドラ（ドロップを許可） */
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  /** パレットからノードがドロップされた時のハンドラ */
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const kindStr = e.dataTransfer.getData('application/node-kind');
      if (!isNodeKind(kindStr)) return;

      const kind = kindStr;
      // マウス位置をフロー座標に変換
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      appendNode(kind, position);
      if (isMobile) {
        setPaletteOpen(false);
      }
    },
    [appendNode, isMobile, screenToFlowPosition]
  );

  const onPalettePointerDrop = useCallback((kind: NodeKind, point: { x: number; y: number }) => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (!rect || point.x < rect.left || point.x > rect.right || point.y < rect.top || point.y > rect.bottom) {
      return;
    }
    appendNode(kind, screenToFlowPosition(point));
    if (isMobile) {
      setPaletteOpen(false);
    }
  }, [appendNode, isMobile, screenToFlowPosition]);

  /** タップ追加（タブレット/モバイル向け） */
  const onPaletteTapAdd = useCallback((kind: NodeKind) => {
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    const offset = TAP_ADD_OFFSETS[tapAddCountRef.current % TAP_ADD_OFFSETS.length];
    tapAddCountRef.current += 1;

    const center = rect
      ? { x: rect.left + rect.width / 2 + offset.x, y: rect.top + rect.height / 2 + offset.y }
      : { x: window.innerWidth / 2 + offset.x, y: window.innerHeight / 2 + offset.y };
    const position = screenToFlowPosition(center);
    appendNode(kind, position);
    if (isMobile) {
      setPaletteOpen(false);
    }
  }, [appendNode, isMobile, screenToFlowPosition]);

  const onNodeClick = useCallback((event: React.MouseEvent, node: AppNode) => {
    setSelectedNodeId(node.id);
    if (isConnectMode) return;
    if (!(event.target instanceof Element) || !event.target.closest('[data-node-settings]')) return;
    if (!isNodeKind(node.type) || !getSettings(node.type)) return;
    setSettingsNodeId(node.id);
  }, [isConnectMode, setSettingsNodeId]);

  /** ダイアログを閉じる。blur 確定の入力欄があるため、閉じる前に確定させる */
  const closeSettings = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    setSettingsNodeId(null);
  }, [setSettingsNodeId]);

  // ジェスチャー作成画面を開いている間は設定ダイアログを隠す（閉じると元の設定に戻る）
  const gestureCreatorRequest = useGestureCreatorRequest();

  /** キャンバスクリック時：選択解除とコンテキストメニュー非表示 */
  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    hideContextMenu();
    if (isMobile) {
      setPaletteOpen(false);
      setInspectorOpen(false);
    }
  }, [hideContextMenu, isMobile]);

  /** エッジ右クリック時：削除メニューを表示 */
  const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: AppEdge) => {
    event.preventDefault();
    showContextMenu({ x: event.clientX, y: event.clientY, type: 'edge', id: edge.id });
  }, [showContextMenu]);

  /** ノード右クリック時：削除メニューを表示（固定ノードは除外） */
  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: AppNode) => {
    if (isFixedNodeId(node.id)) return;  // Start/Endは削除不可
    event.preventDefault();
    showContextMenu({ x: event.clientX, y: event.clientY, type: 'node', id: node.id });
  }, [showContextMenu]);

  const removeElements = useCallback((nodeIds: string[] = [], edgeIds: string[] = []) => {
    const nodeSet = new Set(nodeIds);
    const edgeSet = new Set(edgeIds);

    if (nodeSet.size > 0) {
      setNodes((nds) => nds.filter((node) => !nodeSet.has(node.id)));
      setSelectedNodeId((current) => (current && nodeSet.has(current) ? null : current));
      setSettingsNodeId((current) => (current && nodeSet.has(current) ? null : current));
    }

    if (nodeSet.size > 0 || edgeSet.size > 0) {
      setEdges((eds) =>
        eds.filter(
          (edge) =>
            !edgeSet.has(edge.id) &&
            !nodeSet.has(edge.source) &&
            !nodeSet.has(edge.target)
        )
      );
    }
  }, [setEdges, setNodes, setSettingsNodeId]);

  /** コンテキストメニューから削除を実行 */
  const deleteItem = useCallback(() => {
    if (!contextMenu) return;
    if (contextMenu.type === 'edge') {
      removeElements([], [contextMenu.id]);
    } else {
      removeElements([contextMenu.id]);
    }
    setActionFeedback('カードまたは線を削除しました');
    hideContextMenu();
  }, [contextMenu, hideContextMenu, removeElements]);

  /** エッジハイライトのコールバック */
  const onEdgeHighlight = useCallback((edgeId: string, active: boolean) => {
    setEdges((eds) =>
      eds.map((e) =>
        e.id === edgeId
          ? { ...e, className: active ? 'executing' : undefined }
          : e
      )
    );
  }, [setEdges]);

  const onNodeHighlight = useCallback((nodeId: string, active: boolean) => {
    setActiveNodeIds((current) => {
      const next = new Set(current);
      if (active) next.add(nodeId);
      else next.delete(nodeId);
      return next;
    });
  }, []);

  const finalizeQrScan = useCallback((result: QrScanResult) => {
    const request = qrScanCoordinator.settle(result);
    if (!request) return;
    if (request.cameraToken) {
      void releaseCamera(request.cameraToken);
    }
    setQrScannerOpen(false);
  }, [qrScanCoordinator, releaseCamera]);

  const callListenRpc = useCallback((
    robotId: string,
    service: 'playSound' | 'setLed' | 'micOn' | 'micOff',
    data: unknown,
    label: string,
  ) => {
    return callRobot(robotId, service, data, { timeoutMs: LISTEN_RPC_TIMEOUT_MS })
      .then(() => true)
      .catch((error) => {
        log(`[Listen] ${label}を送信できませんでした: ${String(error)}`);
        return false;
      });
  }, [callRobot, log]);

  const finalizeListen = useCallback((result: ListenResult) => {
    const request = listenCoordinator.settle(result);
    if (!request) return;
    setListenOpen(false);
    setListenStream(null);
    if (request.representativeRobotId) {
      if (request.microphoneEnabled) {
        void callListenRpc(request.representativeRobotId, 'micOff', undefined, 'マイク停止');
      }
      void callListenRpc(request.representativeRobotId, 'setLed', LED_OFF, 'LED消灯');
    }
    if (request.cameraToken) {
      void releaseCamera(request.cameraToken);
    }
  }, [callListenRpc, listenCoordinator, releaseCamera]);

  const playListenStartSound = useCallback(async () => {
    const representativeRobotId = listenCoordinator.current?.representativeRobotId;
    if (!representativeRobotId) return;
    await callListenRpc(
      representativeRobotId,
      'playSound',
      { sound: 'listen' },
      'listen効果音',
    );
  }, [callListenRpc, listenCoordinator]);

  const qrScanner = useMemo<QrScanner>(() => ({
    scan: () => {
      const isSimulation = activeRunModeRef.current === 'simulation';
      const reservation = qrScanCoordinator.reserve(
        isSimulation ? null : remoteSnapshot.representativeRobotId
      );
      if (!reservation) {
        return Promise.resolve({
          status: 'error',
          message: displayLanguage === 'en'
            ? 'QR scan is already running'
            : 'QR読み取りはすでに実行中です',
        } satisfies QrScanResult);
      }

      if (isSimulation) {
        qrScanCoordinator.switchToLocalCamera();
        setQrScannerOpen(true);
        return reservation.result;
      }

      void acquireQrCameraLease(
        qrScanCoordinator,
        reservation.request,
        acquireRepresentativeCamera,
        releaseCamera,
        () => setQrScannerOpen(true),
      ).catch((error) => {
        if (qrScanCoordinator.isCurrent(reservation.request)) {
          log(`[QR Scan] 代表ロボットのカメラを開始できませんでした: ${String(error)}`);
          setQrScannerOpen(true);
        }
      });
      return reservation.result;
    },
  }), [
    acquireRepresentativeCamera,
    displayLanguage,
    log,
    qrScanCoordinator,
    releaseCamera,
    remoteSnapshot.representativeRobotId,
  ]);

  const handleQrLocalFallback = useCallback(() => {
    const token = qrScanCoordinator.switchToLocalCamera();
    if (token) void releaseCamera(token);
  }, [qrScanCoordinator, releaseCamera]);

  const listener = useMemo<SpeechListener>(() => ({
    listen: () => {
      const isSimulation = activeRunModeRef.current === 'simulation';
      const representativeRobotId = isSimulation
        ? null
        : remoteSnapshot.representativeRobotId;
      const reservation = listenCoordinator.reserve(representativeRobotId);
      if (!reservation) {
        return Promise.resolve({
          status: 'error',
          message: displayLanguage === 'en'
            ? 'Listening is already running'
            : '聞き取りはすでに実行中です',
        } satisfies ListenResult);
      }

      if (isSimulation) {
        setListenStream(null);
        setListenOpen(true);
        return reservation.result;
      }

      if (!representativeRobotId) {
        finalizeListen({ status: 'error', message: 'ロボットのマイク音声を受信できません' });
        return reservation.result;
      }

      void (async () => {
        try {
          const cameraToken = await acquireListenCameraLease(
            listenCoordinator,
            reservation.request,
            acquireRepresentativeCamera,
            releaseCamera,
          );
          if (!listenCoordinator.isCurrent(reservation.request)) return;
          if (!cameraToken) {
            finalizeListen({ status: 'error', message: 'ロボットのマイク音声を受信できません' });
            return;
          }

          const microphoneEnabled = await enableListenMicrophone(
            listenCoordinator,
            reservation.request,
            async () => {
              const enabled = await callListenRpc(
                representativeRobotId,
                'micOn',
                undefined,
                'マイク開始',
              );
              if (!enabled) throw new Error('micOn failed');
            },
            async () => {
              await callListenRpc(representativeRobotId, 'micOff', undefined, 'マイク停止');
            },
          );
          if (!microphoneEnabled) return;

          const remoteAudioStream = await waitForLiveAudioStream(
            () => representativeStreamRef.current,
            () => listenCoordinator.isCurrent(reservation.request),
          );
          if (!listenCoordinator.isCurrent(reservation.request)) return;
          if (!remoteAudioStream) {
            finalizeListen({ status: 'error', message: 'ロボットのマイク音声を受信できません' });
            return;
          }

          void callListenRpc(representativeRobotId, 'setLed', LISTENING_LED, 'LED点灯');
          setListenStream(remoteAudioStream);
          setListenOpen(true);
        } catch (error) {
          if (listenCoordinator.isCurrent(reservation.request)) {
            log(`[Listen] 代表ロボットのマイクを開始できませんでした: ${String(error)}`);
            finalizeListen({ status: 'error', message: 'ロボットのマイク音声を受信できません' });
          }
        }
      })();
      return reservation.result;
    },
  }), [
    acquireRepresentativeCamera,
    callListenRpc,
    displayLanguage,
    finalizeListen,
    listenCoordinator,
    log,
    releaseCamera,
    remoteSnapshot.representativeRobotId,
  ]);

  useEffect(() => {
    const request = qrScanCoordinator.current;
    const representativeRobotId = remoteSnapshot.representativeRobotId;
    if (
      !isQrScannerOpen
      || !request
      || request.usingLocalCamera
      || request.representativeRobotId === representativeRobotId
    ) return;
    let active = true;
    void (async () => {
      try {
        if (request.cameraToken) {
          const migrated = await migrateRepresentativeCamera(request.cameraToken);
          if (!active || !qrScanCoordinator.isCurrent(request)) return;
          request.cameraToken = migrated ? request.cameraToken : null;
        } else if (representativeRobotId) {
          const token = await acquireRepresentativeCamera();
          if (!active || !qrScanCoordinator.attachCamera(request, token)) {
            if (token) await releaseCamera(token);
            return;
          }
        }
        request.representativeRobotId = representativeRobotId;
      } catch (error) {
        if (active) log(`[QR Scan] 代表ロボットのカメラを切り替えられませんでした: ${String(error)}`);
      }
    })();
    return () => { active = false; };
  }, [
    acquireRepresentativeCamera,
    isQrScannerOpen,
    log,
    migrateRepresentativeCamera,
    qrScanCoordinator,
    releaseCamera,
    remoteSnapshot.representativeRobotId,
  ]);

  useEffect(() => {
    releaseCameraRef.current = releaseCamera;
    callRobotRef.current = callRobot;
  }, [callRobot, releaseCamera]);

  useEffect(() => {
    return () => {
      const request = qrScanCoordinator.settle({ status: 'cancelled' });
      if (!request) return;
      const { cameraToken } = request;
      if (cameraToken) {
        void releaseCameraRef.current(cameraToken);
      }
    };
  }, [qrScanCoordinator]);

  useEffect(() => {
    return () => {
      const request = listenCoordinator.settle({ status: 'cancelled' });
      if (!request) return;
      if (request.representativeRobotId) {
        void callRobotRef.current(
          request.representativeRobotId,
          'setLed',
          LED_OFF,
          { timeoutMs: LISTEN_RPC_TIMEOUT_MS },
        ).catch(() => undefined);
      }
      if (request.cameraToken) {
        void releaseCameraRef.current(request.cameraToken);
      }
    };
  }, [listenCoordinator]);

  const stopRemoteSpeech = useCallback(() => {
    const service = activeBatchServiceRef.current;
    if (!service || stopSpeakPromiseRef.current) {
      return;
    }
    const request = service.stopSpeak({ nodeId: '__flow_stop__' })
      .then((summary) => {
        summary.results.forEach((result) => {
          const detail = result.message ?? result.errorCode;
          log(`[Stop Speak][${result.robotId}] ${result.status}${detail ? `: ${detail}` : ''}`);
        });
      })
      .catch((error) => {
        log(`[Stop Speak] 停止要求を送信できませんでした: ${String(error)}`);
      })
      .finally(() => {
        flushLogs();
      });
    stopSpeakPromiseRef.current = request;
  }, [flushLogs, log]);

  const cleanupRemoteLeds = useCallback(() => {
    const service = activeBatchServiceRef.current;
    if (!service || ledCleanupPromiseRef.current) {
      return;
    }
    const request = service.cleanupLed({
      nodeId: '__flow_cleanup__',
      timeoutMs: FLOW_EFFECT_RPC_TIMEOUT_MS,
    })
      .then((summary) => {
        if (!isMountedRef.current) return;
        summary.results.forEach((result) => {
          if (result.status === 'fulfilled') return;
          const detail = result.message ?? result.errorCode;
          log(`[Light Cleanup][${result.robotId}] 警告: ${result.status}${detail ? `: ${detail}` : ''}`);
        });
      })
      .catch((error) => {
        if (isMountedRef.current) {
          log(`[Light Cleanup] 警告: 終了時に消灯できませんでした: ${String(error)}`);
        }
      })
      .finally(() => {
        if (isMountedRef.current) flushLogs();
      });
    ledCleanupPromiseRef.current = request;
  }, [flushLogs, log]);

  cleanupRemoteLedsRef.current = cleanupRemoteLeds;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
      cleanupRemoteLedsRef.current();
      runTargetUnsubscribeRef.current?.();
      runTargetUnsubscribeRef.current = null;
    };
  }, []);

  const onStop = useCallback(() => {
    stopFlowExecution({
      abortController: abortControllerRef.current,
      stopRemoteSpeech,
      cleanupRemoteLeds,
      setFlowMotion,
      setSayText,
      setTofScan,
      finalizeQrScan,
      finalizeListen,
    });
  }, [cleanupRemoteLeds, finalizeListen, finalizeQrScan, setFlowMotion, setSayText, setTofScan, stopRemoteSpeech]);

  /** 3Dシミュレーションまたは実機でフローを実行する */
  const onRun = useCallback(async (mode: FlowRunMode) => {
    if (isRunning || abortControllerRef.current || remoteSnapshot.isRunInProgress) {
      return;
    }

    unlockSpeechAudioContext();

    clearLogs();
    setRunWarning(null);
    const connectivityWarning = validateFlowConnectivity(nodes, edges, displayLanguage)
      ?? (OFFLINE_MODE ? findOfflineFlowIssue(nodes, edges, displayLanguage) : null);
    if (connectivityWarning) {
      setRunWarning(connectivityWarning);
      log(connectivityWarning);
      flushLogs();
      return;
    }

    let targets: FlowRunTargets;
    let robotService: BatchRobotService | undefined;
    try {
      if (mode === 'robots') {
        targets = createRunSnapshot();
        robotService = createBatchService(targets);
      } else {
        targets = createSimulationRunTargets();
      }
      setRunInProgress(true, targets);
    } catch (error) {
      log(`実行できません: ${error instanceof Error ? error.message : String(error)}`);
      flushLogs();
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    activeBatchServiceRef.current = robotService ?? null;
    activeRunModeRef.current = mode;
    stopSpeakPromiseRef.current = null;
    ledCleanupPromiseRef.current = null;
    setActiveRunMode(mode);
    setRunTargetCount(mode === 'robots' ? targets.robotIds.length : null);
    setIsRunning(true);
    setActiveNodeIds(new Set());
    if (mode === 'simulation') {
      log('[Simulation] 3Dロボットで実行します');
      setRobotPreviewMinimized(false);
    } else {
      log(`[Run] 対象ロボット: ${targets.robotIds.join(', ')}`);
      let activeRobotIds = new Set(targets.robotIds);
      runTargetUnsubscribeRef.current = subscribeRunTargets(targets, (nextActiveRobotIds) => {
        const next = new Set(nextActiveRobotIds);
        const lostRobotIds = [...activeRobotIds].filter((robotId) => !next.has(robotId));
        lostRobotIds.forEach((robotId) => {
          log(`[Run][${robotId}] disconnected: 接続が失われたため、以後の送信対象から外しました`);
        });
        activeRobotIds = next;
        if (next.size === 0 && !controller.signal.aborted) {
          log('[Run] 実行停止: 接続中の実行対象が0台になりました');
          controller.abort();
          cleanupRemoteLeds();
          setFlowMotion(null);
          setSayText(null);
          finalizeQrScan({ status: 'cancelled' });
          finalizeListen({ status: 'cancelled' });
        }
        if (lostRobotIds.length > 0) flushLogs();
      });
    }
    // 実行前に全エッジのハイライトをリセット
    setEdges((eds) => eds.map((e) => ({ ...e, className: undefined })));
    const runErrorNodeIds = new Set<string>();
    try {
      await runFlow(
        nodes,
        edges,
        log,
        {
          ...(robotService ? {
            robotService,
            getRunStopReason: () => robotService.hasActiveTargets()
              ? null
              : '接続中の対象ロボットが0台になりました',
          } : {}),
          qrScanner,
          listener,
          setFlowMotion,
          setSayText,
          playSimulationSound: mode === 'simulation' ? playSimulationSoundEffect : undefined,
          playSimulationSpeech: mode === 'simulation' ? playSimulationSpeech : undefined,
          synthesizeSpeech: mode === 'simulation' ? synthesizeSpeech : undefined,
          setSimulationLightColor,
          setTofScan,
          measureSimulationTof: mode === 'simulation'
            ? () => measureTofRef.current?.() ?? 0
            : undefined,
          onExecutionEvent: (event) => {
            if (event.type === 'node-error') {
              runErrorNodeIds.add(event.nodeId);
              setErrorNodeIds((current) => new Set(current).add(event.nodeId));
            } else if (event.type === 'node-finished' && !runErrorNodeIds.has(event.nodeId)) {
              setErrorNodeIds((current) => {
                if (!current.has(event.nodeId)) return current;
                const next = new Set(current);
                next.delete(event.nodeId);
                return next;
              });
            }
          },
        },
        onEdgeHighlight,
        controller.signal,
        false,
        onNodeHighlight,
        false,
        displayLanguage,
      );
    } catch (error) {
      log(`エラー: 実行中に もんだいが おきました（${String(error)}）`);
    } finally {
      runTargetUnsubscribeRef.current?.();
      runTargetUnsubscribeRef.current = null;
      cleanupRemoteLeds();
      await Promise.allSettled([
        stopSpeakPromiseRef.current ?? Promise.resolve(),
        ledCleanupPromiseRef.current ?? Promise.resolve(),
      ]);
      activeBatchServiceRef.current = null;
      stopSpeakPromiseRef.current = null;
      ledCleanupPromiseRef.current = null;
      abortControllerRef.current = null;
      setRunInProgress(false);
      activeRunModeRef.current = null;
      setActiveRunMode(null);
      setIsRunning(false);
      setRunTargetCount(null);
      // フロー完了時にモーションとふきだしをリセット
      setFlowMotion(null);
      setSayText(null);
      setTofScan(null);
      setSimulationEyeColor(SIMULATION_LED_OFF_COLOR);
      setActiveNodeIds(new Set());
      setEdges((eds) => (eds.some((e) => e.className) ? eds.map((e) => ({ ...e, className: undefined })) : eds));
      flushLogs();  // バッファに残っているログを強制的に表示
    }
  }, [nodes, edges, log, clearLogs, flushLogs, isRunning, remoteSnapshot.isRunInProgress, createRunSnapshot, createBatchService, setRunInProgress, subscribeRunTargets, qrScanner, listener, setEdges, onEdgeHighlight, onNodeHighlight, setFlowMotion, setSayText, playSimulationSpeech, synthesizeSpeech, setSimulationLightColor, setTofScan, finalizeQrScan, finalizeListen, cleanupRemoteLeds, displayLanguage]);

  const onRunSimulation = useCallback(() => {
    void onRun('simulation');
  }, [onRun]);

  const onRunRobots = useCallback(() => {
    void onRun('robots');
  }, [onRun]);

  const onResetFlow = useCallback(() => {
    if (isRunning) {
      return;
    }
    if (!window.confirm('つくったフローを けして さいしょから にしますか？')) {
      return;
    }

    if (workspaceStack.length === 0) {
      clearSavedFlow(userSub);
    }
    const resetNodes = workspaceStack.length > 0
      ? getInitialFunctionNodes(getCurrentFlowLayoutPreset())
      : getInitialNodes(getCurrentFlowLayoutPreset());
    setNodes(resetNodes);
    setEdges(initialEdges);
    setSelectedNodeId(null);
    setSettingsNodeId(null);
    setConnectMode(false);
    clearLogs();
    setActionFeedback('フローを最初の状態に戻しました');
    hideContextMenu();
    window.requestAnimationFrame(() => {
      void fitView({
        nodes: resetNodes.map(({ id }) => ({ id })),
        padding: 0.2,
        duration: 400,
      });
    });
  }, [clearLogs, fitView, hideContextMenu, isRunning, setEdges, setNodes, setSettingsNodeId, userSub, workspaceStack.length]);

  const onTogglePalette = useCallback(() => {
    setPaletteOpen((prev) => {
      const next = !prev;
      if (next) {
        setInspectorOpen(false);
        hideContextMenu();
      }
      return next;
    });
  }, [hideContextMenu]);

  const onToggleInspector = useCallback(() => {
    setInspectorOpen((prev) => {
      const next = !prev;
      if (next) {
        setPaletteOpen(false);
        hideContextMenu();
      }
      return next;
    });
  }, [hideContextMenu]);

  const onToggleBottomInspector = useCallback(() => {
    setBottomInspectorOpen((open) => !open);
  }, []);

  const onToggleDisplayLanguage = useCallback(() => {
    setDisplayLanguage((prev) => (prev === 'en' ? 'ja' : 'en'));
  }, []);

  const onToggleConnectMode = useCallback(() => {
    setConnectMode((prev) => !prev);
  }, []);

  const onOpenSourceView = useCallback(() => {
    setSourceViewOpen(true);
  }, []);

  const onCloseSourceView = useCallback(() => {
    setSourceViewOpen(false);
  }, []);

  const selectedNodeIds = useMemo(
    () => nodes.filter((node) => node.selected && !isFixedNodeId(node.id)).map((node) => node.id),
    [nodes]
  );

  const selectedEdgeIds = useMemo(
    () => edges.filter((edge) => edge.selected).map((edge) => edge.id),
    [edges]
  );

  const canDeleteSelection = selectedNodeIds.length > 0 || selectedEdgeIds.length > 0;
  const displayNodes = useMemo(() => nodes.map((node) => {
    const classes = [
      ...(node.className ?? '').split(/\s+/).filter((name) => name !== 'executing' && name !== 'execution-error'),
      activeNodeIds.has(node.id) ? 'executing' : null,
      errorNodeIds.has(node.id) ? 'execution-error' : null,
    ].filter(Boolean);
    return { ...node, className: classes.join(' ') };
  }), [activeNodeIds, errorNodeIds, nodes]);
  const isRunBlocked = remoteSnapshot.isConnectionChangeInProgress;
  const isRobotRunBlocked = isRunBlocked || connectedCount === 0;

  const onDeleteSelected = useCallback(() => {
    if (!canDeleteSelection) return;
    removeElements(selectedNodeIds, selectedEdgeIds);
    setActionFeedback('選択したカードまたは線を削除しました');
    hideContextMenu();
  }, [canDeleteSelection, hideContextMenu, removeElements, selectedEdgeIds, selectedNodeIds]);

  const applyHistorySnapshot = useCallback((snapshot: FlowHistorySnapshot | null) => {
    if (!snapshot || isRunning) return;
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setSelectedNodeId(null);
    setSettingsNodeId(null);
    setConnectMode(false);
    setActionFeedback(null);
    hideContextMenu();
  }, [hideContextMenu, isRunning, setEdges, setNodes, setSettingsNodeId]);

  const onUndo = useCallback(() => applyHistorySnapshot(undoHistory()), [applyHistorySnapshot, undoHistory]);
  const onRedo = useCallback(() => applyHistorySnapshot(redoHistory()), [applyHistorySnapshot, redoHistory]);

  useEffect(() => {
    const handleHistoryShortcut = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return;
      const target = event.target;
      if (target instanceof HTMLElement && (
        target.isContentEditable
        || target.matches('input, textarea, select')
      )) return;
      event.preventDefault();
      if (event.shiftKey) onRedo();
      else onUndo();
    };
    window.addEventListener('keydown', handleHistoryShortcut);
    return () => window.removeEventListener('keydown', handleHistoryShortcut);
  }, [onRedo, onUndo]);

  return (
    <FlowDisplayLanguageProvider value={displayLanguage}>
      <div className="relative flex h-full min-w-0 flex-col">
        <div className="flex min-w-0 flex-1 flex-col">
          <FlowToolbar
            brandSlot={toolbarSlots?.brand}
            trailingSlot={toolbarSlots?.trailing}
            languageControl={{
              displayLanguage,
              onToggle: onToggleDisplayLanguage,
            }}
            historyControl={{
              canUndo: canUndo && !isRunning,
              canRedo: canRedo && !isRunning,
              onUndo,
              onRedo,
            }}
            runControl={{
              isRunning,
              activeMode: activeRunMode,
              simulationDisabled: isRunBlocked,
              robotsDisabled: isRobotRunBlocked,
              connectedCount,
              targetCount: runTargetCount ?? connectedCount,
              onRunSimulation,
              onRunRobots,
              onStop,
            }}
            connectControl={{
              enabled: isConnectMode,
              onToggle: onToggleConnectMode,
            }}
            deleteControl={{
              enabled: canDeleteSelection,
              onDelete: onDeleteSelected,
            }}
            resetControl={{
              disabled: isRunning,
              onReset: onResetFlow,
            }}
            guideControl={workspaceStack.length === 0 ? {
              onOpen: () => setGuideOpen(true),
            } : undefined}
            sourceControl={{
              onOpen: onOpenSourceView,
              buttonRef: sourceButtonRef,
            }}
            mobileControls={isMobile ? {
              isPaletteOpen,
              isInspectorOpen,
              onTogglePalette,
              onToggleInspector,
            } : undefined}
          />
          <div data-product-tour="flow-canvas" className="flex min-h-0 flex-1 flex-col">
            <FlowCanvas
              nodes={displayNodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onNodeClick={onNodeClick}
              onNodeDoubleClick={onNodeClick}
              onNodeDragStart={onNodeDragStart}
              onNodeDragStop={onNodeDragStop}
              onPaneClick={onPaneClick}
              onEdgeContextMenu={onEdgeContextMenu}
              onNodeContextMenu={onNodeContextMenu}
              viewportPreset={currentPreset}
              connectMode={isConnectMode}
              touchMode={isTouchMode}
              containerRef={canvasContainerRef}
            >
              {workspaceStack.length > 0 && (
                <div className="function-workspace-badge" aria-live="polite">
                  関数ワークスペース: {workspaceStack[workspaceStack.length - 1].functionName}
                </div>
              )}
              {workspaceStack.length > 0 && (
                <nav className="function-breadcrumbs" aria-label="作業場の階層">
                  <button type="button" onClick={returnToRootWorkspace} className="function-breadcrumbs__root">
                    <Home aria-hidden="true" size={15} />ルート
                  </button>
                  {workspaceStack.map((frame) => (
                    <span key={frame.functionNodeId} className="function-breadcrumbs__item">
                      <ChevronRight aria-hidden="true" size={14} />
                      {frame.functionName}
                    </span>
                  ))}
                </nav>
              )}
              {isConnectMode && (
                <div className="pointer-events-none absolute left-1/2 top-20 z-20 -translate-x-1/2 animate-[fade-drop_200ms_ease-out] rounded-2xl border border-blue-300 bg-white/95 px-4 py-2 text-center text-sm font-medium text-blue-800 shadow-lg">
                  つなぐモード: つなぎたい丸を押してから、つぎの丸を押してください
                </div>
              )}
              {isMobile && isPaletteOpen && (
                <div className={`flow-card-dock flow-card-dock--mobile${showCardNames ? '' : ' is-icon-only'}`}>
                  <NodePalette
                    className="h-full w-full"
                    layout="dock"
                    hideFunctionCard={workspaceStack.length > 0}
                    onTapAdd={enableTapPaletteAdd ? onPaletteTapAdd : undefined}
                    onPointerDrop={onPalettePointerDrop}
                    displayLanguage={displayLanguage}
                    showCardNames={showCardNames}
                    onToggleCardNames={() => setShowCardNames((visible) => !visible)}
                  />
                </div>
              )}
              {isMobile && isInspectorOpen && (
                <div
                  className="absolute inset-y-0 right-0 z-30 border-l border-border bg-card shadow-lg"
                  style={{ width: 'min(85vw, 320px)' }}
                >
                  <FlowInspectorPanel
                    help={help}
                    selectedNode={selectedNode}
                    logs={logs}
                    priority={isRunning ? 'logs' : 'description'}
                    className="h-full w-full"
                    onClearLogs={clearLogs}
                  />
                </div>
              )}
              <div
                className={`flow-robot-preview${showDockedCamera ? ' has-camera' : ''}`}
                style={{ pointerEvents: isRobotPreviewMinimized ? 'none' : undefined }}
              >
                <div
                  className={`flow-robot-preview__robot-layer${isRobotPreviewMinimized || showDockedCamera ? ' is-hidden' : ''}`}
                  aria-hidden={isRobotPreviewMinimized || showDockedCamera}
                >
                  <Suspense fallback={<div className="virtual-robot"><div className="virtual-robot__status">3D表示を準備中</div></div>}>
                    <VirtualRobot
                      paused={isRobotPreviewMinimized || showDockedCamera || isIntroductionPresentation}
                      eyeLedColor={simulationEyeColor}
                      onTofMeasureReady={onTofMeasureReady}
                    />
                  </Suspense>
                </div>
                {hasDockedCamera && (
                  <div
                    className={`flow-robot-preview__camera-layer${showDockedCamera ? '' : ' is-hidden'}`}
                    aria-hidden={!showDockedCamera}
                  >
                    {robotCameraView?.render({ hidden: !showDockedCamera })}
                  </div>
                )}
                {isRobotPreviewMinimized ? (
                  <button
                    onClick={() => setRobotPreviewMinimized(false)}
                    aria-label="ロボットプレビューを表示"
                    className="workspace-panel-control pointer-events-auto absolute right-0 top-0 flex size-10 items-center justify-center rounded-full"
                  >
                    <Bot />
                  </button>
                ) : (
                  <button
                    onClick={() => setRobotPreviewMinimized(true)}
                    aria-label="ロボットプレビューを最小化"
                    className="absolute -top-1 -right-1 z-10 flex size-7 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
                  >
                    <Minus className="size-4" />
                  </button>
                )}
              </div>
              {!isMobile && (
                <div className={`flow-card-dock${showCardNames ? '' : ' is-icon-only'}`}>
                  <NodePalette
                    className="h-full w-full"
                    layout="dock"
                    hideFunctionCard={workspaceStack.length > 0}
                    onTapAdd={enableTapPaletteAdd ? onPaletteTapAdd : undefined}
                    onPointerDrop={onPalettePointerDrop}
                    displayLanguage={displayLanguage}
                    showCardNames={showCardNames}
                    onToggleCardNames={() => setShowCardNames((visible) => !visible)}
                  />
                </div>
              )}
            </FlowCanvas>
            {!isMobile && isBottomInspectorOpen && (
              <div className="flow-description-panel shrink-0 overflow-hidden border-t border-border bg-background">
                <FlowInspectorPanel
                  help={help}
                  selectedNode={selectedNode}
                  logs={logs}
                  priority={isRunning ? 'logs' : 'description'}
                  className="h-full"
                  onClearLogs={clearLogs}
                />
              </div>
            )}
          </div>
          <footer className="flow-editor-footer">
            {!isMobile && (
              <div className="flow-editor-footer__controls">
                <button
                  type="button"
                  onClick={onToggleBottomInspector}
                  aria-expanded={isBottomInspectorOpen}
                  aria-label={isBottomInspectorOpen ? '説明・ログを縮小' : '説明・ログを開く'}
                  title={isBottomInspectorOpen ? '説明・ログを縮小' : '説明・ログを開く'}
                >
                  {isBottomInspectorOpen
                    ? <ChevronDown aria-hidden="true" size={14} />
                    : <ChevronUp aria-hidden="true" size={14} />}
                  {isBottomInspectorOpen ? '説明・ログを縮小' : '説明・ログを開く'}
                </button>
              </div>
            )}
            <div className="flow-editor-footer__latest-log" aria-live="polite" title={logs[logs.length - 1] ?? undefined}>
              {logs.length > 0 ? logs[logs.length - 1] : '最新ログはありません'}
            </div>
            <button
              type="button"
              onClick={saveStatus === 'error' ? saveCurrentFlow : undefined}
              aria-label={saveStatus === 'error' ? '保存できません。再試行' : saveStatus === 'saving' ? '保存中' : '保存済み'}
              title={saveError ?? (savedAt ? `保存済み ${savedAt}` : '保存状態')}
              className={`flow-editor-footer__save flow-editor-footer__save--${saveStatus}`}
            >
              {saveStatus === 'saving' ? '保存中' : saveStatus === 'error' ? '保存できません' : '保存済み'}
              {saveStatus === 'saved' && savedAt && <span>{savedAt}</span>}
            </button>
          </footer>
          <ContextMenu contextMenu={contextMenu} onDelete={deleteItem} />
          {actionFeedback && (
            <div role="status" className="absolute bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white shadow-xl">
              <span>{actionFeedback}</span>
              <button type="button" onClick={onUndo} disabled={!canUndo} className="font-bold text-blue-200 hover:text-white disabled:opacity-50">
                元に戻す
              </button>
              <button type="button" onClick={() => setActionFeedback(null)} aria-label="通知を閉じる" className="text-slate-300 hover:text-white">
                ×
              </button>
            </div>
          )}
          {runWarning && (
            <div role="alert" className="absolute bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-xl">
              <span>{runWarning}</span>
              <button
                type="button"
                onClick={() => setRunWarning(null)}
                aria-label="警告を閉じる"
                className="text-red-100 hover:text-white"
              >
                ×
              </button>
            </div>
          )}
        </div>
        <QrScannerDialog
          open={isQrScannerOpen}
          remoteStream={representativeStream}
          preferLocalCamera={activeRunMode === 'simulation'}
          onLocalFallback={handleQrLocalFallback}
          onResult={finalizeQrScan}
        />
        <ListenDialog
          open={isListenOpen}
          stream={listenStream}
          getCredentials={getTranscribeCredentials}
          playStartSound={playListenStartSound}
          onResult={finalizeListen}
        />
        {isSourceViewOpen && (
          <FlowSourceViewer
            source={generatedSource}
            onClose={onCloseSourceView}
            returnFocusRef={sourceButtonRef}
          />
        )}
        {settingsNode && settingsKind && settingsContent && !gestureCreatorRequest.open && (
          <NodeSettingsDialog
            node={settingsNode}
            title={getNodeDisplayLabel(settingsKind, displayLanguage)}
            content={settingsContent}
            onClose={closeSettings}
          />
        )}
        {gestureCreatorRequest.open && (
          <GestureCreatorOverlay
            userSub={userSub}
            initialMotionId={gestureCreatorRequest.motionId}
            startTour={gestureCreatorRequest.startTour}
            onClose={closeGestureCreator}
          />
        )}
        {isGuideOpen && (
          <ProductTour
            steps={FLOW_TOUR_STEPS}
            completionLabel="さあ、始めましょう"
            onClose={() => setGuideOpen(false)}
          />
        )}
      </div>
    </FlowDisplayLanguageProvider>
  );
}

/**
 * フローエディタのエクスポート用コンポーネント
 * ReactFlowProviderでラップして必要なコンテキストを提供
 */
export function FlowEditor({
  duplicateFlowRequest,
  onWorkspaceContextChange,
  onFlowSnapshotChange,
  onDisplayLanguageChange,
  toolbarSlots,
  robotCameraView,
}: FlowEditorProps) {
  return (
    <ReactFlowProvider>
      <FlowEditorInner
        duplicateFlowRequest={duplicateFlowRequest}
        onWorkspaceContextChange={onWorkspaceContextChange}
        onFlowSnapshotChange={onFlowSnapshotChange}
        onDisplayLanguageChange={onDisplayLanguageChange}
        toolbarSlots={toolbarSlots}
        robotCameraView={robotCameraView}
      />
    </ReactFlowProvider>
  );
}
